import { useCallback, useMemo } from 'react';
import { LogbookTemplateEditData } from '../types/loogBookTypes';
import { useCreateOrUpdateLogbookTemplate } from './useCreateOrUpdateLogbookTemplate';
import { useLogbookFormState } from './useLogbookFormState';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';

interface UseLogbookFormProps {
  initialData?: LogbookTemplateEditData;
  onClose: () => void;
}

export const useLogbookForm = ({ initialData, onClose }: UseLogbookFormProps) => {
  const {
    formState,
    handleInputChange,
    handleFileSelect,
    handleFileRemove,
    handleRotate,
  } = useLogbookFormState(initialData);
  const queryClient = useQueryClient();
  const mutation = useCreateOrUpdateLogbookTemplate({
    onSuccess: () => {
      toast.success(initialData
        ? 'Logbook template updated successfully'
        : 'Logbook template created successfully');
        queryClient.invalidateQueries({ queryKey: ['logbookTemplates'] });
        queryClient.invalidateQueries({ queryKey: ['logbookTemplate', initialData?.logbookTemplateId] });
        onClose(); 
    },
  });

  const { isPending, error: mutationError } = mutation;

  const isFormValid = useMemo(() => {
    const { name, description, type, category, previewUrl } = formState;
    return (
      name.trim() !== '' &&
      description.trim() !== '' &&
      type.trim() !== '' &&
      category.trim() !== '' &&
      previewUrl !== null
    );
  }, [formState]);

  const handleSubmit = useCallback(() => {
    if (!isFormValid) return;
    const formData = new FormData();
    formData.append('name', formState.name);
    formData.append('description', formState.description);
    formData.append('type', formState.type);
    formData.append('category', formState.category);
    if (formState.logbookTemplateId) {
      formData.append('logbookTemplateId', formState.logbookTemplateId);
    }
    
    if (formState.file) {
      formData.append('file', formState.file);
    } else if (formState.attachmentId) {
      formData.append('attachmentId', formState.attachmentId);
    }

    mutation.mutate(formData);
  }, [isFormValid, formState, mutation]);

  const actions = useMemo(() => ([
    {
      text: 'Cancel',
      variant: 'outlined',
      color: 'primary',
      onClick: onClose,
      disabled: isPending,
    },
    {
      text: isPending ? 'Saving...' : (initialData ? 'Update' : 'Submit'),
      variant: 'contained',
      color: 'primary',
      onClick: handleSubmit,
      disabled: !isFormValid || isPending,
    },
  ]), [isPending, isFormValid, initialData, onClose, handleSubmit]);

  return {
    formState,
    actions,
    isPending,
    mutationError,
    handleInputChange,
    handleFileSelect,
    handleFileRemove,
    handleRotate,
  };
};